import { vault } from './sync/vault.js';
import { index } from './worker/client.js';
import { useApp } from './store.js';
import type { WorkerFileInput, WorkerNoteInput } from './worker/api.js';

/** Wire vault -> index worker and vault -> store after login. Idempotent. */
let booted = false;

export async function bootVault(): Promise<void> {
  if (booted) return;
  booted = true;

  await vault.start();
  vault.provider.onStatus((s) => useApp.getState().setSyncStatus(s));

  // .base files are Y.Text docs (kind 'note') holding view-config JSON — the
  // index must treat them as plain files, not searchable/graphable notes.
  const isIndexableNote = (e: { kind: string; path: string }) =>
    e.kind === 'note' && e.path.endsWith('.md');

  const fileInputs = (): WorkerFileInput[] =>
    [...vault.filesSnapshot().entries()]
      .filter(([, e]) => !e.deleted)
      .map(([id, e]) => ({
        id,
        path: e.path,
        kind: e.kind === 'note' && !isIndexableNote(e) ? 'file' : e.kind,
        ctime: e.ctime,
      }));

  const noteInput = (id: string): WorkerNoteInput | null => {
    const e = vault.getEntry(id);
    if (!e || e.deleted || !isIndexableNote(e)) return null;
    return { id, path: e.path, content: vault.noteContent(id), ctime: e.ctime };
  };

  // Full snapshot first… but never let an index-worker failure block the app
  // shell — vault browsing/editing works without search/graph.
  const notes = [...vault.filesSnapshot().entries()]
    .map(([id]) => noteInput(id))
    .filter((n): n is WorkerNoteInput => n !== null);
  try {
    await index.replaceAll(notes, fileInputs());
  } catch (err) {
    console.error('[bootstrap] index worker unavailable — search/graph disabled', err);
  }

  // …then incremental: meta changes re-send the file list (renames change
  // resolution), content changes upsert the single note (debounced).
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  // A meta change (create/rename/delete/remote update) only needs the file
  // list refreshed — setFiles updates paths/titles and resolution and drops
  // vanished ids; per-note content re-index is driven by onNoteContent below.
  // (Re-upserting every note here would be O(n) structured-clones per change.)
  vault.onFiles(() => {
    void index.setFiles(fileInputs());
  });
  vault.onNoteContent((id) => {
    clearTimeout(pending.get(id));
    pending.set(
      id,
      setTimeout(() => {
        pending.delete(id);
        const n = noteInput(id);
        if (n) void index.upsertNote(n);
      }, 300),
    );
  });
}
